import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Send, Mail, Zap, Hand } from "lucide-react";

const STATUS_STYLES = {
  sent: { label: "Envoyé", cls: "bg-[#F5EFD8] text-forest border-forest/20" },
  failed: { label: "Échec", cls: "bg-[#FCE7E7] text-[#9F1239] border-[#9F1239]/20" },
  queued_mock: { label: "En attente", cls: "bg-af-subtle text-af-muted border-af" },
};

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export default function DistributionsPage() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api
      .get("/distributions")
      .then(({ data }) => setItems(data))
      .catch((err) => {
        console.error("Failed to load distributions:", err);
        setItems([]);
      });
  }, []);

  if (items === null) return <div className="p-8 text-af-muted">Chargement de l'historique…</div>;

  return (
    <div className="min-h-full p-6 md:p-10 max-w-5xl mx-auto" data-testid="distributions-page">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-terracotta font-semibold mb-2">Distribution</div>
        <h1 className="font-display font-extrabold text-3xl md:text-4xl">Historique des envois</h1>
        <p className="text-af-secondary mt-2">
          Tous les comptes-rendus PDF envoyés par email, automatiquement ou manuellement.
        </p>
      </div>

      {items.length === 0 ? (
        <Card className="p-12 bg-af-card border-af rounded-md text-center" data-testid="distributions-empty">
          <Mail className="h-10 w-10 text-af-muted mx-auto mb-4" />
          <p className="text-af font-semibold mb-1">Aucun envoi pour le moment</p>
          <p className="text-sm text-af-muted">
            Terminez une réunion avec des participants : le compte-rendu leur sera envoyé automatiquement.
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((d) => {
            const st = STATUS_STYLES[d.status] || STATUS_STYLES.queued_mock;
            return (
              <Card key={d.id} className="p-5 bg-af-card border-af rounded-md" data-testid={`distribution-row-${d.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="h-9 w-9 rounded-md bg-af-subtle border border-af flex items-center justify-center shrink-0">
                      <Send className="h-4 w-4 text-terracotta" />
                    </div>
                    <div className="min-w-0">
                      <Link
                        to={`/app/meetings/${d.meeting_id}`}
                        className="font-semibold text-af hover:text-terracotta transition-colors truncate block"
                        data-testid={`distribution-meeting-link-${d.id}`}
                      >
                        {d.meeting_title}
                      </Link>
                      <div className="text-xs text-af-muted mt-0.5">{formatDate(d.created_at)}</div>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {(d.recipients || []).map((r) => (
                          <span key={r} className="text-xs bg-af-subtle border border-af rounded-full px-2.5 py-0.5 text-af-secondary">
                            {r}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant="outline" className="rounded-full border-af text-af-secondary gap-1">
                      {d.trigger === "auto" ? <Zap className="h-3 w-3" /> : <Hand className="h-3 w-3" />}
                      {d.trigger === "auto" ? "Automatique" : "Manuel"}
                    </Badge>
                    <Badge variant="outline" className={`rounded-full ${st.cls}`} data-testid={`distribution-status-${d.id}`}>
                      {st.label}
                    </Badge>
                  </div>
                </div>
                {d.status === "failed" && d.note && (
                  <div className="mt-3 text-xs text-[#9F1239] bg-[#FCE7E7] border border-[#9F1239]/20 rounded-md px-3 py-2">
                    {d.note}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
